import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";

const MENU_GAP = 4;
const ESTIMATED_MENU_HEIGHT = 260; // rough cap, only used to decide flip-up vs flip-down

// Accessible dropdown/menu primitive — shared by ProjectCardMenu, DocumentCardMenu,
// and anything else that needs a "kebab" style menu. Handles:
//  - roving keyboard focus (ArrowUp/Down, Home/End) between [role="menuitem"] children
//  - Escape to close + return focus to the trigger
//  - click-outside to close
//  - aria-expanded/aria-haspopup wiring via the returned refs
//  - fixed-position placement anchored to the trigger's real screen position —
//    pair with <Teleport to="body"> in the component so the menu is never
//    clipped by an ancestor's overflow:hidden (cards routinely have that, to
//    round off a thumbnail image)
//
// Usage: bind `triggerEl` to the trigger button ref, `menuEl` to the menu container
// ref, bind `:style="menuStyle"` on the (teleported, position:fixed) menu, call
// `toggle`/`open`/`close`, and put `@keydown="onMenuKeydown"` on the menu.
// Menu items just need `role="menuitem"` — real <button> elements already get
// Enter/Space activation for free from the browser.
export function useMenu() {
  const isOpen = ref(false);
  const triggerEl = ref(null);
  const menuEl = ref(null);
  const activeIndex = ref(-1);
  const menuStyle = ref({});

  function itemEls() {
    return menuEl.value ? Array.from(menuEl.value.querySelectorAll('[role="menuitem"]:not([disabled])')) : [];
  }

  function updatePosition() {
    if (!triggerEl.value) return;
    const rect = triggerEl.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < ESTIMATED_MENU_HEIGHT && rect.top > spaceBelow;
    menuStyle.value = {
      position: "fixed",
      right: `${Math.max(8, window.innerWidth - rect.right)}px`,
      ...(openUpward
        ? { bottom: `${window.innerHeight - rect.top + MENU_GAP}px` }
        : { top: `${rect.bottom + MENU_GAP}px` }),
    };
  }

  function open() {
    isOpen.value = true;
    updatePosition();
    nextTick(() => {
      const items = itemEls();
      activeIndex.value = items.length ? 0 : -1;
      items[0]?.focus();
    });
  }

  function close({ returnFocus = true } = {}) {
    if (!isOpen.value) return;
    isOpen.value = false;
    activeIndex.value = -1;
    if (returnFocus) triggerEl.value?.focus();
  }

  function toggle() {
    if (isOpen.value) close({ returnFocus: false });
    else open();
  }

  function moveFocus(delta) {
    const items = itemEls();
    if (!items.length) return;
    activeIndex.value = (activeIndex.value + delta + items.length) % items.length;
    items[activeIndex.value]?.focus();
  }

  function onMenuKeydown(e) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveFocus(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveFocus(-1);
        break;
      case "Home":
        e.preventDefault();
        activeIndex.value = 0;
        itemEls()[0]?.focus();
        break;
      case "End": {
        e.preventDefault();
        const items = itemEls();
        activeIndex.value = items.length - 1;
        items[items.length - 1]?.focus();
        break;
      }
      case "Escape":
        e.preventDefault();
        close();
        break;
      case "Tab":
        close({ returnFocus: false });
        break;
    }
  }

  function onDocClick(e) {
    if (!isOpen.value) return;
    if (menuEl.value?.contains(e.target) || triggerEl.value?.contains(e.target)) return;
    close({ returnFocus: false });
  }

  // The menu is teleported/fixed rather than tracking the trigger, so rather
  // than continuously repositioning, just close it if the page scrolls/resizes
  // underneath it (same behavior the legacy context menu used).
  function onScrollOrResize() {
    if (isOpen.value) close({ returnFocus: false });
  }

  onMounted(() => {
    document.addEventListener("click", onDocClick, true);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
  });
  onBeforeUnmount(() => {
    document.removeEventListener("click", onDocClick, true);
    window.removeEventListener("scroll", onScrollOrResize, true);
    window.removeEventListener("resize", onScrollOrResize);
  });

  return { isOpen, triggerEl, menuEl, menuStyle, open, close, toggle, onMenuKeydown };
}
